import { Box } from "@mui/material";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Flex from "../../../../../../../../components/Flex";
import { useGraphAssessment } from "../../../../../../../../queries/patient-chart/assessment/summary/use-graph-assessment";
import { useGraphFilledAssessment } from "../../../../../../../../queries/patient-chart/assessment/summary/use-graph-filled-assessment";
import AssessmentLegendCheckBoxes from "./AssessmentLegendCheckBoxes";
import AssessmentLineChart from "./AssessmentLineChart";
import { AssessmentTitle } from "./AssessmentTitle";
import { SelectDeselectAll } from "./SelectDeSelectAll";
import AssessmentsToolBar from "./components/AssessmentsToolBar";
import dayjs from "dayjs";

// const graphAssessment1 = {
//   data: {
//     "11/28": [
//       {
//         date: "2025-11-28T14:15:23.504Z",
//         displayDate: "11/28",
//         attemptedQuestions: 9,
//         totalQuestions: 10,
//         baselineScore: "0",
//         lastScore: null,
//         operation: null,
//         pointChange: null,
//         AAS: "moderate",
//         Score: 0,
//       },
//       {
//         date: "2025-11-28T14:15:23.504Z",
//         displayDate: "11/28",
//         attemptedQuestions: 9,
//         totalQuestions: 10,
//         baselineScore: "0",
//         lastScore: null,
//         operation: null,
//         pointChange: null,
//         name: "SMSP",
//         SMSP: "high",
//         Score: 0,
//       },
//       {
//         date: "2025-11-28T14:16:53.314Z",
//         displayDate: "11/28",
//         attemptedQuestions: 7,
//         totalQuestions: 9,
//         baselineScore: "0",
//         lastScore: "0",
//         operation: "increase",
//         pointChange: 5,
//         Score: 5,
//         PHQ9: "low",
//         name: "PHQ9",
//         Severity: "low",
//       },
//       {
//         date: "2025-11-28T14:17:13.459Z",
//         displayDate: "11/28",
//         attemptedQuestions: 16,
//         totalQuestions: 18,
//         baselineScore: "0",
//         lastScore: "5",
//         operation: "increase",
//         pointChange: 43,
//         Score: 48,
//         name: "AAS",
//         AAS: "very_high",
//       },
//     ],
//     "11/29": [
//       {
//         date: "2025-11-28T14:17:13.459Z",
//         displayDate: "11/28",
//         attemptedQuestions: 16,
//         totalQuestions: 18,
//         baselineScore: "0",
//         lastScore: "5",
//         operation: "increase",
//         pointChange: 43,
//         Score: 48,
//         name: "PHQ9",
//         PHQ9: "very_high",
//       },
//       {
//         date: "2025-11-29T14:19:05.365Z",
//         displayDate: "11/29",
//         attemptedQuestions: 14,
//         totalQuestions: 18,
//         baselineScore: "0",
//         lastScore: "48",
//         operation: "decrease",
//         pointChange: 34,
//         Score: 14,
//         name: "AAS",
//         AAS: "low",
//       },
//       {
//         date: "2025-11-28T14:15:23.504Z",
//         displayDate: "11/29",
//         attemptedQuestions: 9,
//         totalQuestions: 10,
//         baselineScore: "0",
//         lastScore: null,
//         operation: null,
//         pointChange: null,
//         name: "AAS",
//         AAS: "moderate",
//         Score: 0,
//       },
//     ],
//     "12/11": [
//       {
//         date: "2025-12-11T07:34:51.681Z",
//         displayDate: "12/11",
//         attemptedQuestions: 10,
//         totalQuestions: 10,
//         baselineScore: "0",
//         lastScore: null,
//         operation: null,
//         pointChange: null,
//         Score: 5,
//         name: "ACES",
//         ACES: "moderate",
//       },
//       {
//         date: "2025-11-28T14:15:23.504Z",
//         displayDate: "12/11",
//         attemptedQuestions: 9,
//         totalQuestions: 10,
//         baselineScore: "0",
//         lastScore: null,
//         operation: null,
//         pointChange: null,
//         name: "AAS",
//         AAS: "moderate",
//         Score: 0,
//       },
//     ],
//   },
// };

const AssessmentSummaryGraph = () => {
  const { patientId } = useParams();
  const { data: submittedAssessments } = useGraphFilledAssessment(
    patientId as string
  );
  const opts = submittedAssessments?.data ?? ({} as any);
  const [selectedAssessments, setSelectedAssessments] = useState<
    Record<string, boolean>
  >({});

  useEffect(() => {
    if (submittedAssessments?.data) {
      setSelectedAssessments(
        Object.keys(submittedAssessments?.data ?? {}).reduce(
          (acc, key) => {
            acc[key] = true;
            return acc;
          },
          {} as Record<string, boolean>
        )
      );
    }
  }, [submittedAssessments?.data]);

  const [timeRange, setTimeRange] = useState<{
    fromDate: string;
    toDate: string;
  }>({
    fromDate: dayjs().startOf("month").toISOString(),
    toDate: dayjs().endOf("month").toISOString(),
  });

  const { data: graphAssessment } = useGraphAssessment({
    patientId: patientId as string,
    screenerTypes: Object.keys(selectedAssessments ?? {}).filter(
      (key) => !!selectedAssessments[key]
    ),
    monthFilter: {
      fromDate: timeRange?.fromDate,
      toDate: timeRange?.toDate,
    },
  });
  const toggleAssessment = (name: string) => {
    setSelectedAssessments((prev) => ({
      ...prev,
      [name]: !prev[name],
    }));
  };

  const selectAll = () => {
    const allTrue: Record<string, boolean> = {};
    Object.keys(selectedAssessments).forEach((key) => {
      allTrue[key] = true;
    });
    setSelectedAssessments(allTrue);
  };

  const deselectAll = () => {
    const allFalse: Record<string, boolean> = {};
    Object.keys(selectedAssessments).forEach((key) => {
      allFalse[key] = false;
    });
    setSelectedAssessments(allFalse);
  };

  return (
    <>
      <Flex justifyContent="space-between" alignItems="end">
        <AssessmentTitle />

        <AssessmentsToolBar
          onChange={(range) => {
            setTimeRange({
              fromDate: range.from.toISOString(),
              toDate: range.to.toISOString(),
            });
          }}
        />
      </Flex>
      <Box sx={{ width: "100%", height: 300, mb: 3 }}>
        <AssessmentLineChart
          data={graphAssessment?.data ?? {}}
          assessmentConfig={submittedAssessments?.data as any}
          // selectedAssessments={selectedAssessments}
        />
      </Box>

      {!!Object.keys(opts).length && (
        <SelectDeselectAll selectAll={selectAll} deselectAll={deselectAll} />
      )}

      <AssessmentLegendCheckBoxes
        options={opts}
        selectedAssessments={selectedAssessments}
        toggleAssessment={toggleAssessment}
      />
    </>
  );
};

export default AssessmentSummaryGraph;
